import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Badge, Button, Form, Pagination } from "react-bootstrap";
import useApi from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import AddStudent from "./AddStudent";
import EditStudent from "./EditStudent";
import "./VendorStudent.css"; // Scoped CSS import

const VendorStudent = () => {
  const { user } = useAuth();
  const { bearerAuth } = useApi();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const vendorId = user?.id;

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const res = await bearerAuth("/admin/users", "GET");
        const allUsers = res?.data?.data?.data || [];

        // filter students registered by this vendor
        const vendorStudents = allUsers.filter(
          (u) => u?.register_by === vendorId
        );

        setStudents(vendorStudents);
      } catch (err) {
        console.log("Error fetching vendor students:", err);
      } finally {
        setLoading(false);
      }
    };

    if (vendorId) fetchStudents();
  }, [vendorId, refresh]);

  useEffect(() => {
    setCurrentPage(1);
  }, [fromDate, toDate, refresh]);

  const handleEditClick = (student) => {
    setSelectedStudent(student);
    setShowEditModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setRefresh(!refresh);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedStudent(null);
    setRefresh(!refresh);
  };

  const toDateKey = (value) => {
    if (!value) return "";
    const raw = String(value).slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : "";
  };

  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      const rowDate = toDateKey(
        s.created_at || s.latest_enrollment?.created_at || s.latest_enrollment?.expected_start_date
      );
      const matchesFromDate = !fromDate || (rowDate && rowDate >= fromDate);
      const matchesToDate = !toDate || (rowDate && rowDate <= toDate);
      return matchesFromDate && matchesToDate;
    });
  }, [students, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedStudents = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    return filteredStudents.slice(start, start + itemsPerPage);
  }, [filteredStudents, safePage]);

  if (loading) {
    return <h4 className="vendor-loading">Loading...</h4>;
  }

  return (
    <div className="vendor-student-container">
      <div className="vendor-student-header">
        <h2 className="vendor-student-title">My Students</h2>
        <Button
          variant="primary"
          onClick={() => setShowAddModal(true)}
          className="vendor-add-button"
          >
          <i className="bi bi-plus-circle"></i> Add Student
        </Button>
      </div>

      <Card className="mb-3">
        <Card.Body className="d-flex flex-column flex-md-row gap-2 align-items-md-center flex-wrap">
          <Form.Control
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            max={toDate || undefined}
            style={{ maxWidth: 220 }}
          />
          <Form.Control
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            min={fromDate || undefined}
            style={{ maxWidth: 220 }}
          />
          <Button
            variant="outline-secondary"
            onClick={() => {
              setFromDate("");
              setToDate("");
            }}
            disabled={!fromDate && !toDate}
          >
            Clear Filters
          </Button>
        </Card.Body>
      </Card>

      <Card className="vendor-student-card">
        <Card.Body className="vendor-card-body">
          {/* Desktop Table View */}
          <div className="vendor-desktop-table">
            <Table striped bordered hover responsive className="vendor-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student Id</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Course</th>
                  <th>School</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredStudents.length === 0 && (
                  <tr>
                    <td colSpan="9" className="vendor-empty-state">
                      No students found
                    </td>
                  </tr>
                )}

                {paginatedStudents.map((s, index) => (
                  <tr key={s.id}>
                    <td>{(safePage - 1) * itemsPerPage + index + 1}</td>
                    <td>{s.id || "N/A"}</td>
                    <td>{s.full_name || s.username || "N/A"}</td>
                    <td>{s.contact || "N/A"}</td>
                    <td>{s.email || "N/A"}</td>
                    <td>{s?.enrollment?.course?.title || "N/A"}</td>
                    <td>{s?.enrollment?.driving_school?.name || "N/A"}</td>
                    <td>
                      <Badge 
                        bg={s.status === 1 ? "success" : "secondary"} 
                        className="vendor-badge"
                      >
                        {s.status === 1 ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td>
                      <div className="vendor-action-buttons">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditClick(s)}
                          title="Edit Student"
                          className="vendor-edit-button"
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        {/* Delete button removed */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="vendor-mobile-cards">
            {paginatedStudents.length === 0 ? (
              <div className="vendor-mobile-empty-state">
                No students found
              </div>
            ) : (
              paginatedStudents.map((s, index) => (
                <div key={s.id} className="vendor-student-item">
                  <div className="vendor-card-header">
                    <span className="vendor-student-name">
                      {s.full_name || s.username || "N/A"}
                    </span>
                    <Badge 
                      bg={s.status === 1 ? "success" : "secondary"} 
                      className="vendor-mobile-badge"
                    >
                      {s.status === 1 ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  
                  <div className="vendor-card-details">
                    <div className="vendor-detail-row">
                      <span className="vendor-detail-label">Student ID:</span>
                      <span className="vendor-detail-value">{s.id || "N/A"}</span>
                    </div>
                    <div className="vendor-detail-row">
                      <span className="vendor-detail-label">Phone:</span>
                      <span className="vendor-detail-value">{s.contact || "N/A"}</span>
                    </div>
                    <div className="vendor-detail-row">
                      <span className="vendor-detail-label">Email:</span>
                      <span className="vendor-detail-value">{s.email || "N/A"}</span>
                    </div>
                    <div className="vendor-detail-row">
                      <span className="vendor-detail-label">Course:</span>
                      <span className="vendor-detail-value">
                        {s?.enrollment?.course?.title || "N/A"}
                      </span>
                    </div>
                    <div className="vendor-detail-row">
                      <span className="vendor-detail-label">School:</span>
                      <span className="vendor-detail-value">
                        {s?.enrollment?.driving_school?.name || "N/A"}
                      </span>
                    </div>
                  </div>
                  
                  <div className="vendor-card-actions">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEditClick(s)}
                      className="vendor-mobile-edit-button"
                    >
                      <i className="bi bi-pencil vendor-mobile-button-icon"></i>
                      Edit Student
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          {filteredStudents.length > 0 && (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
              <div className="text-muted small">
                Showing {Math.min(filteredStudents.length, (safePage - 1) * itemsPerPage + 1)} to{" "}
                {Math.min(filteredStudents.length, safePage * itemsPerPage)} of {filteredStudents.length} students
              </div>
              <Pagination className="mb-0">
                <Pagination.Prev
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={safePage === 1}
                />
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                  <Pagination.Item
                    key={page}
                    active={page === safePage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={safePage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add Student Modal */}
      {showAddModal && <AddStudent onClose={handleCloseAddModal} />}

      {/* Edit Student Modal */}
      {showEditModal && selectedStudent && (
        <EditStudent student={selectedStudent} onClose={handleCloseEditModal} />
      )}
    </div>
  );
};

export default VendorStudent;
